import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import AmazonEventsManager from './AmazonEventsManager';
import { analyzeSalesEstimate, getJungleScoutTokens } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
    listSalesEstimateBaskets,
    createSalesEstimateBasket,
    updateSalesEstimateBasket,
    deleteSalesEstimateBasket,
    getAmazonEvents
} from '../../services/salesEstimateService';

// --- Basket management state ---
const COUNTRIES = ['US', 'MX', 'CA', 'UK', 'DE', 'ES', 'FR', 'IT'];

// ─── Marketplace → Amazon domain mapping ───
const MARKETPLACE_DOMAINS = {
    us: 'amazon.com',
    mx: 'amazon.com.mx',
    ca: 'amazon.ca',
    uk: 'amazon.co.uk',
    de: 'amazon.de',
    fr: 'amazon.fr',
    it: 'amazon.it',
    es: 'amazon.es',
};



// ─── Color palette matching tracking2 ───
const BRAND_COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

const EMPTY_RESULT = {
    weekly: [],
    highlights: null,
    failures: [],
    dateRange: { startDate: '-', endDate: '-' },
    tokensUsed: 0,
    tokensRemaining: 0,
};

function SalesEstimate() {
        // Baskets
        const [baskets, setBaskets] = useState([]);
        const [selectedBasket, setSelectedBasket] = useState(null);
        const [showNew, setShowNew] = useState(false);
        const [showEdit, setShowEdit] = useState(false);
        const [newName, setNewName] = useState('');
        const [newAsins, setNewAsins] = useState('');
        const [newCountry, setNewCountry] = useState('US');
        const [editing, setEditing] = useState(null);
        const [editName, setEditName] = useState('');
        const [editAsins, setEditAsins] = useState('');
        const [editCountry, setEditCountry] = useState('US');
        const accountId = localStorage.getItem('selectedAccountValue') || '';

        // Load baskets on mount
        useEffect(() => {
            (async () => {
                try {
                    const res = await listSalesEstimateBaskets(accountId);
                    setBaskets(res.data || []);
                } catch (err) {}
            })();
        }, [accountId]);

        // Select basket and parse ASINs
        const handleSelectBasket = (basket) => {
            setSelectedBasket(basket);
            setFile(null);
            setFileAsins([]);
            let asinsArr = [];
            if (Array.isArray(basket.asins)) {
                asinsArr = basket.asins;
            } else if (typeof basket.asins === 'string') {
                // Intentar parsear como JSON array
                try {
                    const parsed = JSON.parse(basket.asins);
                    if (Array.isArray(parsed)) {
                        asinsArr = parsed;
                    } else {
                        asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                    }
                } catch {
                    asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                }
            }
            // Unir líneas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < asinsArr.length; i++) {
                const curr = asinsArr[i];
                if (typeof curr === 'object' && curr.asin) {
                    merged.push(curr);
                } else if (typeof curr === 'string') {
                    if (curr.includes(',')) {
                        merged.push(curr);
                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                        merged.push(curr + ',' + asinsArr[i + 1]);
                        i++;
                    } else {
                        merged.push(curr);
                    }
                }
            }
            // Convertir a objetos {asin, brand}
            const parsedAsins = merged.map(a => {
                if (typeof a === 'object' && a.asin) {
                    return { asin: a.asin, brand: a.brand || 'Unknown' };
                }
                if (typeof a === 'string') {
                    const [asin, ...brandParts] = a.split(',');
                    const brand = brandParts.join(',').trim();
                    return { asin: (asin || '').trim(), brand: brand || 'Unknown' };
                }
                return { asin: '', brand: 'Unknown' };
            }).filter(a => a.asin.length >= 10);
            setParsedAsins(parsedAsins);

            // Populate the text area and switch to text input mode for manual verification/edit
            const textRepr = parsedAsins.map(a => `${a.asin}, ${a.brand}`).join('\n');
            setTextAsins(textRepr);
            setInputType('text');
        };

        // Create basket
        const handleCreate = async (e) => {
            e.preventDefault();
            const asins = newAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
            if (!newName || asins.length === 0) {
                setError('You must enter a name and at least one ASIN.');
                return;
            }
            setLoading(true);
            setError('');
            try {
                await createSalesEstimateBasket({ name: newName, asins, country: newCountry, account_id: accountId });
                setNewName(''); setNewAsins(''); setShowNew(false);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
            } catch (err) {
                setError('Error creating basket: ' + (err.message || JSON.stringify(err)));
            } finally {
                setLoading(false);
            }
        };

        // Edit basket
        const openEdit = (basket) => {
            setEditing(basket);
            setEditName(basket.name || '');
            setEditCountry(basket.country || 'US');
            let asinsArr = [];
            if (Array.isArray(basket.asins)) {
                asinsArr = basket.asins;
            } else if (typeof basket.asins === 'string') {
                try {
                    const parsed = JSON.parse(basket.asins);
                    if (Array.isArray(parsed)) {
                        asinsArr = parsed;
                    } else {
                        asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                    }
                } catch {
                    asinsArr = basket.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                }
            }
            // Unir líneas sueltas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < asinsArr.length; i++) {
                const curr = asinsArr[i];
                if (typeof curr === 'object' && curr.asin) {
                    merged.push(`${curr.asin},${curr.brand || ''}`);
                } else if (typeof curr === 'string') {
                    if (curr.includes(',')) {
                        merged.push(curr);
                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                        merged.push(`${curr},${asinsArr[i + 1]}`);
                        i++; // skip next
                    } else {
                        merged.push(curr);
                    }
                }
            }
            setEditAsins(merged.join('\n'));
            setShowEdit(true);
        };
        const handleEdit = async (e) => {
            e.preventDefault();
            if (!editing) {
                setError('No basket selected for editing.');
                return;
            }
            const asins = editAsins.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
            setLoading(true);
            setError('');
            try {
                await updateSalesEstimateBasket(editing.id, { name: editName, asins, country: editCountry });
                setShowEdit(false); setEditing(null);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
            } catch (err) {
                setError('Error editing basket: ' + (err.message || JSON.stringify(err)));
            } finally {
                setLoading(false);
            }
        };
        // Delete basket
        const handleDelete = async (id) => {
            if (!window.confirm('Delete this basket?')) return;
            setLoading(true);
            setError('');
            try {
                await deleteSalesEstimateBasket(id);
                const res = await listSalesEstimateBaskets(accountId);
                setBaskets(res.data || []);
                setSelectedBasket(null);
                setParsedAsins([]);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
    const [file, setFile] = useState(null);
    const [marketplace, setMarketplace] = useState('us');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [metricMode, setMetricMode] = useState('sales_amount');
    const [parsedAsins, setParsedAsins] = useState([]);
    const safeResult = result ?? EMPTY_RESULT;

    // Multi-method input state variables
    const [inputType, setInputType] = useState('file'); // 'file' or 'text'
    const [fileAsins, setFileAsins] = useState([]);
    const [textAsins, setTextAsins] = useState('');
    
    // New feature states
    const [dateRange, setDateRange] = useState('4weeks');
    const [amazonEvents, setAmazonEvents] = useState([]);
    const [showEventsManager, setShowEventsManager] = useState(false);

    // Load token info on mount (shared endpoint with TrackingOverview)
    const loadTokenInfo = async () => {
        try {
            const res = await getJungleScoutTokens();
            if (res && res.remaining !== undefined) {
                setTokenInfo({ used: res.used, limit: res.limit, remaining: res.remaining });
            }
        } catch (e) {
            console.warn('Could not load token info', e);
        }
    };

    const loadAmazonEvents = async () => {
        try {
            const res = await getAmazonEvents();
            if (res && res.data) {
                setAmazonEvents(res.data);
            }
        } catch (err) {
            console.error("Failed to load Amazon Events:", err);
        }
    };

    useEffect(() => {
        loadTokenInfo();
        loadAmazonEvents();
    }, []);

    // Parse file when selected
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) {
            setFile(null);
            setFileAsins([]);
            if (inputType === 'file') setParsedAsins([]);
            return;
        }
        setFile(selectedFile);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target.result;
            let lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line && !line.startsWith('#'));
            // Ignorar encabezado si existe
            if (lines.length && /^asin[,;\t ]*brand/i.test(lines[0])) {
                lines = lines.slice(1);
            }
            // Unir líneas tipo ASIN\nBrand en ASIN,Brand
            const merged = [];
            for (let i = 0; i < lines.length; i++) {
                const curr = lines[i];
                if (curr.includes(',')) {
                    merged.push(curr);
                } else if (i + 1 < lines.length && !lines[i + 1].includes(',')) {
                    merged.push(`${curr},${lines[i + 1]}`);
                    i++;
                } else {
                    merged.push(curr);
                }
            }
            // Parsear ASIN,Brand
            const asins = merged.map(line => {
                const [asin, ...brandParts] = line.split(',');
                const brand = brandParts.join(',').trim();
                return { asin: (asin || '').trim(), brand: brand || 'Unknown' };
            }).filter(a => a.asin.length >= 10);

            // Validar formato
            const invalidLines = merged.filter(line => {
                const [asin] = line.split(',');
                return !asin || asin.length < 10;
            });
            if (invalidLines.length > 0) {
                setError('Some lines do not match the ASIN,Brand format or contain an invalid ASIN. Example: B07PXGQC1Q,Coop Home Goods');
            } else {
                setError(null);
            }
            setFileAsins(asins);
            setParsedAsins(asins);
        };
        reader.readAsText(selectedFile);
    };

    const updateParsedAsinsFromText = (text) => {
        let lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line && !line.startsWith('#'));
        const asins = lines.map(line => {
            if (line.includes(',')) {
                const [asin, ...brandParts] = line.split(',');
                const brand = brandParts.join(',').trim();
                return { asin: (asin || '').trim(), brand: brand || 'Unknown' };
            } else {
                return { asin: line, brand: 'Unknown' };
            }
        }).filter(a => a.asin.length >= 10);

        const invalidLines = lines.filter(line => {
            const [asin] = line.split(',');
            return !asin || asin.trim().length < 10;
        });

        if (invalidLines.length > 0) {
            setError('Some lines contain an invalid ASIN (minimum 10 characters).');
        } else {
            setError(null);
        }
        setParsedAsins(asins);
    };

    const handleTextAsinsChange = (e) => {
        const val = e.target.value;
        setTextAsins(val);
        updateParsedAsinsFromText(val);
    };

    const handleTabChange = (type) => {
        setInputType(type);
        if (type === 'text') {
            updateParsedAsinsFromText(textAsins);
        } else {
            setParsedAsins(fileAsins);
        }
    };

    const handleSubmit = async () => {
        if (!parsedAsins.length) {
            setError(inputType === 'file' ? 'File contains no valid ASINs (minimum 10 characters per ASIN)' : 'Input contains no valid ASINs (minimum 10 characters per ASIN)');
            return;
        }
        if (parsedAsins.length > 10) {
            setError(`Maximum 10 ASINs per request. You have configured ${parsedAsins.length}.`);
            return;
        }

        setLoading(true);
        setError(null);
        setResult(EMPTY_RESULT);

        // Date Range Calculation
        let reqStart = undefined;
        let reqEnd = undefined;
        
        if (dateRange !== '4weeks') {
            const now = new Date();
            // Find last finished Thursday (JS API billing standard)
            const day = now.getDay(); // 0=Sun
            let deltaToThu = (day - 4 + 7) % 7;
            if (deltaToThu === 0) deltaToThu = 7;
            const lastThu = new Date(now);
            lastThu.setDate(now.getDate() - deltaToThu);
            lastThu.setHours(0, 0, 0, 0);
            
            const start = new Date(lastThu);
            if (dateRange === '3months') start.setMonth(start.getMonth() - 3);
            else if (dateRange === '6months') start.setMonth(start.getMonth() - 6);
            else if (dateRange === '1year') start.setFullYear(start.getFullYear() - 1);
            
            // Align start to the preceding Friday so chunks are full weeks
            const startDay = start.getDay();
            let deltaToFri = (startDay - 5 + 7) % 7;
            start.setDate(start.getDate() - deltaToFri);
            
            reqStart = start.toISOString().slice(0, 10);
            reqEnd = lastThu.toISOString().slice(0, 10);
        }

        console.log(`[SalesEstimate] Run Analysis for Range: ${dateRange}`);
        console.log(`[SalesEstimate] reqStart: ${reqStart || 'Default (4 weeks)'}, reqEnd: ${reqEnd || 'Default'}`);

        try {
            const res = await analyzeSalesEstimate(parsedAsins, marketplace, reqStart, reqEnd);
            if (res.success) {
                setResult(res.data);
                // Refresh token count from API (same source of truth as TrackingOverview)
                await loadTokenInfo();
            } else {
                setError(res.message);
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCSV = () => {
        if (!safeResult.weekly || safeResult.weekly.length === 0) return;
        
        const headers = ['Week End', 'Brand', 'ASIN', 'Units Sold', 'Sales ($)', 'Avg Price ($)'];
        const csvRows = [headers.join(',')];
        
        safeResult.weekly.forEach(row => {
            const r = [
                row.week_end,
                `"${row.brand}"`,
                row.asin,
                row.units_sold,
                row.sales_amount,
                row.avg_price.toFixed(2)
            ];
            csvRows.push(r.join(','));
        });
        
        const csvContent = "data:text/csv;charset=utf-8," + encodeURIComponent(csvRows.join('\n'));
        const link = document.createElement("a");
        link.setAttribute("href", csvContent);
        link.setAttribute("download", `sales_estimate_${marketplace}_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ─── Token badge color ───
    const tokenBadgeClass = () => {
        if (!tokenInfo) return 'bg-secondary';
        const pct = tokenInfo.used / tokenInfo.limit;
        if (pct >= 0.9) return 'bg-danger';
        if (pct >= 0.7) return 'bg-warning text-dark';
        return 'bg-success';
    };

    // ─── Build overview chart data ───
    const buildOverviewData = () => {
        if (!safeResult.weekly.length) return null;

        const metricLabel = metricMode === 'sales_amount' ? 'Sales Amount (USD)' : 'Units Sold';
        const hoverFmt = metricMode === 'sales_amount' ? '$%{y:,.0f}' : '%{y:,}';

        const brandData = {};
        for (const row of safeResult.weekly) {
            if (!brandData[row.brand]) brandData[row.brand] = [];
            brandData[row.brand].push(row);
        }

        const traces = Object.entries(brandData).map(([brand, rows], i) => {
            const sorted = [...rows].sort((a, b) => a.week_end.localeCompare(b.week_end));
            const weekAgg = {};
            for (const r of sorted) {
                if (!weekAgg[r.week_end]) weekAgg[r.week_end] = 0;
                weekAgg[r.week_end] += r[metricMode];
            }

            return {
                x: Object.keys(weekAgg),
                y: Object.values(weekAgg),
                type: 'scatter',
                mode: 'lines+markers',
                name: brand,
                line: { color: BRAND_COLORS[i % BRAND_COLORS.length] },
                hovertemplate: `Brand: ${brand}<br>Value: ${hoverFmt}<br>Week end: %{x}<extra></extra>`,
            };
        });

        return {
            traces,
            layout: {
                title: `Weekly Overview — ${metricLabel} by Brand (Fri→Thu)`,
                height: 450,
                hovermode: 'x unified',
                xaxis: { title: { text: 'Week Ending (Thursday)' }, type: 'date' },
                yaxis: { title: { text: metricLabel } },
                margin: { l: 60, r: 20, t: 50, b: 55 },
                legend: { title: { text: 'Brand' } },
            },
        };
    };

    // ─── Build breakdown (small multiples) ───
    const buildBreakdownData = () => {
        if (!safeResult.weekly.length) return null;

        const brands = [...new Set(safeResult.weekly.map(r => r.brand))];
        const metricLabel = metricMode === 'sales_amount' ? 'Sales (USD)' : 'Units';
        const hoverFmt = metricMode === 'sales_amount' ? '$%{y:,.0f}' : '%{y:,}';

        return brands.map((brand, i) => {
            const rows = safeResult.weekly.filter(r => r.brand === brand);
            const weekAgg = {};
            for (const r of rows) {
                if (!weekAgg[r.week_end]) weekAgg[r.week_end] = 0;
                weekAgg[r.week_end] += r[metricMode];
            }
            const sortedWeeks = Object.keys(weekAgg).sort();

            return {
                brand,
                trace: {
                    x: sortedWeeks,
                    y: sortedWeeks.map(w => weekAgg[w]),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: brand,
                    line: { color: BRAND_COLORS[i % BRAND_COLORS.length] },
                    hovertemplate: `${brand}<br>${metricLabel}: ${hoverFmt}<br>%{x}<extra></extra>`,
                },
            };
        });
    };

    // ─── Build Market Share Data (Donut) ───
    const buildMarketShareData = () => {
        if (!safeResult.weekly.length) return null;
        const brandAgg = {};
        for (const r of safeResult.weekly) {
            if (!brandAgg[r.brand]) brandAgg[r.brand] = 0;
            brandAgg[r.brand] += r[metricMode];
        }
        return {
            data: [{
                values: Object.values(brandAgg),
                labels: Object.keys(brandAgg),
                type: 'pie',
                hole: 0.4,
                textinfo: 'label+percent',
                marker: { colors: BRAND_COLORS.slice(0, Object.keys(brandAgg).length) },
                hovertemplate: metricMode === 'sales_amount' ? '<b>%{label}</b><br>$%{value:,.0f} (%{percent})<extra></extra>' : '<b>%{label}</b><br>%{value:,} Units (%{percent})<extra></extra>'
            }],
            layout: {
                margin: { l: 20, r: 20, t: 20, b: 20 },
                showlegend: false
            }
        };
    };

    // ─── Detect Seasonal Events ───
    const detectSeasonalEvent = (dateStr) => {
        if (!dateStr || amazonEvents.length === 0) return '';
        const date = new Date(dateStr);
        // Remove time component for comparison
        date.setHours(0,0,0,0);
        
        for (const evt of amazonEvents) {
            const start = new Date(evt.start_date);
            // In JavaScript new Date('YYYY-MM-DD') evaluates to UTC midnight. We want local equivalent for naive comparison.
            start.setMinutes(start.getMinutes() + start.getTimezoneOffset());
            start.setHours(0,0,0,0);
            
            const end = new Date(evt.end_date);
            end.setMinutes(end.getMinutes() + end.getTimezoneOffset());
            end.setHours(23,59,59,999);
            
            if (date >= start && date <= end) {
                return evt.event_name;
            }
        }
        return '';
    };

    // ─── Build Elasticity Data (Scatter) ───
    const buildElasticityData = () => {
        if (!safeResult.weekly.length) return null;
        
        const annotations = [];
        const brands = [...new Set(safeResult.weekly.map(r => r.brand))];
        const traces = brands.map((brand, i) => {
            const rows = safeResult.weekly.filter(r => r.brand === brand && r.units_sold > 0 && r.avg_price > 0);
            
            // Calculate Elasticity Coefficient
            let elasticity = null;
            if (rows.length >= 2) {
                let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
                const n = rows.length;
                rows.forEach(r => {
                    sumX += r.avg_price;
                    sumY += r.units_sold;
                    sumXY += r.avg_price * r.units_sold;
                    sumX2 += r.avg_price * r.avg_price;
                });
                const denominator = (n * sumX2 - sumX * sumX);
                if (denominator !== 0) {
                    const slope = (n * sumXY - sumX * sumY) / denominator;
                    const meanX = sumX / n;
                    const meanY = sumY / n;
                    if (meanY !== 0) elasticity = slope * (meanX / meanY);
                }
            }

            const label = elasticity !== null ? `${brand} (E: ${elasticity.toFixed(2)})` : brand;

            // Generate Annotations for points during events
            rows.forEach(r => {
                const eventName = detectSeasonalEvent(r.week_end || r.date);
                if (eventName) {
                    annotations.push({
                        x: r.avg_price,
                        y: r.units_sold,
                        text: `<b>${eventName}</b>`,
                        showarrow: true,
                        arrowhead: 2,
                        arrowsize: 1,
                        arrowwidth: 1.5,
                        arrowcolor: '#ef4444',
                        ax: 0,
                        ay: -35,
                        bgcolor: 'rgba(239, 68, 68, 0.9)',
                        font: { color: '#ffffff', size: 10, family: 'Inter, sans-serif' },
                        bordercolor: '#b91c1c',
                        borderwidth: 1,
                        borderpad: 3,
                        bordercolor: '#ef4444'
                    });
                }
            });

            const brandColor = BRAND_COLORS[i % BRAND_COLORS.length];

            return {
                x: rows.map(r => r.avg_price),
                y: rows.map(r => r.units_sold),
                customdata: rows.map(r => r.week_end || r.date),
                text: rows.map(r => {
                    const event = detectSeasonalEvent(r.week_end || r.date);
                    return event ? `<br><b style="color: #fbbf24;">🌟 ${event}</b>` : '';
                }),
                mode: 'markers',
                type: 'scatter',
                name: label,
                marker: { color: brandColor, size: 8, opacity: 0.7 },
                hovertemplate: `<span style="color:${brandColor};">●</span> <b>${brand}</b><br>Week: %{customdata}%{text}<br>Price: $%{x:.2f}<br>Units: %{y:,}<br>Elasticity: ${elasticity !== null ? elasticity.toFixed(2) : 'N/A'}<extra></extra>`,
                hoverlabel: {
                    bgcolor: '#1e293b',
                    font: { color: '#f8fafc', family: 'Inter, system-ui, sans-serif', size: 12 },
                    bordercolor: '#334155'
                }
            };
        });

        return {
            data: traces,
            layout: {
                margin: { l: 50, r: 20, t: 20, b: 80 },
                xaxis: { title: 'Avg Price ($)', gridcolor: '#f1f5f9' },
                yaxis: { title: 'Units Sold', gridcolor: '#f1f5f9' },
                plot_bgcolor: 'rgba(0,0,0,0)',
                paper_bgcolor: 'rgba(0,0,0,0)',
                showlegend: true,
                legend: { orientation: 'h', y: -0.25 },
                annotations: annotations
            }
        };
    };

    // ─── Highlights ───
    const renderHighlights = () => {
        if (!safeResult.highlights) return null;
        const { sales, variation, units } = safeResult.highlights;

        return (
            <div className="row mb-4 g-4">
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
                        <div className="position-absolute opacity-25" style={{ top: '-15px', right: '-15px', fontSize: '9rem' }}>
                            <i className="bi bi-currency-dollar"></i>
                        </div>
                        <div className="card-body text-white position-relative z-1 p-4">
                            <h6 className="card-title fw-bold text-uppercase mb-4 d-flex align-items-center gap-2" style={{ letterSpacing: '1px', opacity: 0.9 }}>
                                <i className="bi bi-wallet2 fs-5"></i> Sales
                            </h6>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Highest</small>
                                    <div className="fw-bold fs-5 text-truncate" style={{ maxWidth: '150px' }}>{sales.highest.brand}</div>
                                </div>
                                <div className="fs-4 fw-bolder">${sales.highest.value.toLocaleString()}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Lowest</small>
                                    <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }}>{sales.lowest.brand}</div>
                                </div>
                                <div className="fw-semibold">${sales.lowest.value.toLocaleString()}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Average</small>
                                </div>
                                <div className="fw-bold">${sales.average.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
                        <div className="position-absolute opacity-25" style={{ top: '-15px', right: '-15px', fontSize: '9rem' }}>
                            <i className="bi bi-graph-up-arrow"></i>
                        </div>
                        <div className="card-body text-white position-relative z-1 p-4">
                            <h6 className="card-title fw-bold text-uppercase mb-4 d-flex align-items-center gap-2" style={{ letterSpacing: '1px', opacity: 0.9 }}>
                                <i className="bi bi-bar-chart-line fs-5"></i> Week-over-Week
                            </h6>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Highest</small>
                                    <div className="fw-bold fs-5 text-truncate" style={{ maxWidth: '150px' }}>{variation.highest.brand}</div>
                                </div>
                                <div className="fs-4 fw-bolder">{variation.highest.value}%</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Lowest</small>
                                    <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }}>{variation.lowest.brand}</div>
                                </div>
                                <div className="fw-semibold">{variation.lowest.value}%</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Average</small>
                                </div>
                                <div className="fw-bold">{variation.average}%</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
                        <div className="position-absolute opacity-25" style={{ top: '-15px', right: '-15px', fontSize: '9rem' }}>
                            <i className="bi bi-box-seam"></i>
                        </div>
                        <div className="card-body text-white position-relative z-1 p-4">
                            <h6 className="card-title fw-bold text-uppercase mb-4 d-flex align-items-center gap-2" style={{ letterSpacing: '1px', opacity: 0.9 }}>
                                <i className="bi bi-box fs-5"></i> Units Sold
                            </h6>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Most</small>
                                    <div className="fw-bold fs-5 text-truncate" style={{ maxWidth: '150px' }}>{units.most.brand}</div>
                                </div>
                                <div className="fs-4 fw-bolder">{units.most.value.toLocaleString()}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom border-white border-opacity-25">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Least</small>
                                    <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }}>{units.least.brand}</div>
                                </div>
                                <div className="fw-semibold">{units.least.value.toLocaleString()}</div>
                            </div>
                            <div className="d-flex justify-content-between align-items-end">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Average</small>
                                </div>
                                <div className="fw-bold">{units.average.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const overviewData = buildOverviewData();
    const breakdownData = buildBreakdownData();
    const marketShareData = buildMarketShareData();
    const elasticityData = buildElasticityData();

    // ─── WoW Variations for Data Table ───
    const wowVars = useMemo(() => {
        if (!safeResult.weekly.length) return {};
        const varMap = {};
        
        const byAsin = {};
        for (const r of safeResult.weekly) {
            if (!byAsin[r.asin]) byAsin[r.asin] = [];
            byAsin[r.asin].push(r);
        }

        for (const asin in byAsin) {
            const rows = byAsin[asin].sort((a, b) => (a.week_end || '').localeCompare(b.week_end || ''));
            for (let i = 1; i < rows.length; i++) {
                const prev = rows[i - 1];
                const curr = rows[i];
                
                const dCurr = new Date(curr.week_end);
                const dPrev = new Date(prev.week_end);
                const diffDays = Math.round((dCurr - dPrev) / (1000 * 60 * 60 * 24));
                
                if (diffDays === 7) {
                    const unitsPct = prev.units_sold === 0 ? 0 : ((curr.units_sold - prev.units_sold) / prev.units_sold) * 100;
                    const salesPct = prev.sales_amount === 0 ? 0 : ((curr.sales_amount - prev.sales_amount) / prev.sales_amount) * 100;
                    varMap[`${asin}|${curr.week_end}`] = { unitsPct, salesPct };
                }
            }
        }
        return varMap;
    }, [safeResult.weekly]);

    const renderVar = (val) => {
        if (val === undefined) return null;
        if (val > 0) return <span className="text-success ms-2 fw-bold" style={{ fontSize: '0.75rem' }}>▲ {Math.round(val)}%</span>;
        if (val < 0) return <span className="text-danger ms-2 fw-bold" style={{ fontSize: '0.75rem' }}>▼ {Math.abs(Math.round(val))}%</span>;
        return <span className="text-muted ms-2 fw-bold" style={{ fontSize: '0.75rem' }}>- 0%</span>;
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* Basket selector and management */}
                <div className="mb-3 d-flex align-items-center flex-wrap">
                    <div className="dropdown me-3">
                        <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="bi bi-basket2 me-1"></i> Select basket
                        </button>
                        <ul className="dropdown-menu">
                            {baskets.map(b => (
                                <li key={b.id}>
                                    <button className="dropdown-item d-flex justify-content-between align-items-center"
                                        style={selectedBasket?.id === b.id ? {
                                            backgroundColor: '#eaf2ff',
                                            borderLeft: '3px solid #0d6efd',
                                            color: '#1f2937'
                                        } : undefined}
                                        onClick={() => handleSelectBasket(b)}>
                                        <span>{b.name} ({(() => {
                                            let asinsArr = [];
                                            if (Array.isArray(b.asins)) {
                                                asinsArr = b.asins;
                                            } else if (typeof b.asins === 'string') {
                                                try {
                                                    const arr = JSON.parse(b.asins);
                                                    if (Array.isArray(arr)) {
                                                        asinsArr = arr;
                                                    } else {
                                                        asinsArr = b.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                                                    }
                                                } catch {
                                                    asinsArr = b.asins.split(/\n/).map(s => s.trim()).filter(Boolean);
                                                }
                                            }
                                            // Merge ASIN\nBrand lines into ASIN,Brand
                                            const merged = [];
                                            for (let i = 0; i < asinsArr.length; i++) {
                                                const curr = asinsArr[i];
                                                if (typeof curr === 'object' && curr.asin) {
                                                    merged.push(curr);
                                                } else if (typeof curr === 'string') {
                                                    if (curr.includes(',')) {
                                                        merged.push(curr);
                                                    } else if (i + 1 < asinsArr.length && typeof asinsArr[i + 1] === 'string' && !asinsArr[i + 1].includes(',')) {
                                                        merged.push(curr + ',' + asinsArr[i + 1]);
                                                        i++;
                                                    } else {
                                                        merged.push(curr);
                                                    }
                                                }
                                            }
                                            return merged.length;
                                        })()} ASINs)</span>
                                        <span className="d-flex align-items-center gap-2 ms-3">
                                            <i className="bi bi-pencil-square text-primary" title="Edit basket" onClick={e => { e.stopPropagation(); openEdit(b); }}></i>
                                            <i className="bi bi-trash text-danger" onClick={e => { e.stopPropagation(); handleDelete(b.id); }}></i>
                                        </span>
                                    </button>
                                </li>
                            ))}
                            <li><hr className="dropdown-divider" /></li>
                            <li><button className="dropdown-item" onClick={() => setShowNew(true)}>
                                <i className="bi bi-plus-lg me-1"></i> New basket
                            </button></li>
                        </ul>
                    </div>
                    {selectedBasket && (
                        <span className="badge bg-primary px-3 py-2 me-auto" style={{ fontSize: '0.9rem' }}>
                            Selected: <strong>{selectedBasket.name}</strong>
                        </span>
                    )}
                    
                    <button 
                        className="btn btn-outline-secondary ms-auto" 
                        onClick={() => setShowEventsManager(true)}
                        title="Manage Amazon Seasonal Events Calendar"
                    >
                        <i className="bi bi-calendar-event me-1"></i> Seasonal Events
                    </button>
                </div>
                {/* New Basket modal */}
            {showNew && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Create New Basket</h5>
                                <button type="button" className="btn-close" onClick={() => setShowNew(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleCreate}>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Name</label>
                                        <input type="text" className="form-control" value={newName} onChange={e => setNewName(e.target.value)} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Country</label>
                                        <select className="form-select" value={newCountry} onChange={e => setNewCountry(e.target.value)}>
                                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">ASINs</label>
                                        <textarea className="form-control" rows="5" value={newAsins} onChange={e => setNewAsins(e.target.value)} placeholder="One ASIN per line" required></textarea>
                                        <div className="form-text">Paste up to 10 ASINs (Format: ASIN,Brand)</div>
                                    </div>
                                    <button type="submit" className="btn btn-primary w-100" disabled={loading}>{loading ? 'Saving...' : 'Create'}</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                {/* Edit Basket modal */}
                {showEdit && (
                    <div className="card mb-4 border-info">
                        <div className="card-body">
                            <h6>Edit Basket</h6>
                            <form onSubmit={handleEdit}>
                                <div className="row g-2">
                                    <div className="col-md-3">
                                        <input className="form-control form-control-sm" placeholder="Name" value={editName} onChange={e => setEditName(e.target.value)} required />
                                    </div>
                                    <div className="col-md-5">
                                        <textarea className="form-control form-control-sm" rows={2} placeholder="ASIN,Brand per line. Ex: B07PXGQC1Q,Coop Home Goods" value={editAsins} onChange={e => setEditAsins(e.target.value)} required />
                                    </div>
                                    <div className="col-md-2">
                                        <select className="form-select form-select-sm" value={editCountry} onChange={e => setEditCountry(e.target.value)}>
                                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2 d-flex gap-1">
                                        <button className="btn btn-info btn-sm" type="submit">Save</button>
                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => { setShowEdit(false); setEditing(null); }}>Cancel</button>
                                    </div>
                                </div>
                            </form>
                            {error && <div className="alert alert-danger mt-2">{error}</div>}
                        </div>
                    </div>
                )}
                <div className="text-center mb-4">
                    <h2 className="fw-bold mb-1 text-slate-800" style={{ fontSize: '1.75rem', color: '#0f172a' }}>Sales Estimate</h2>
                    <p className="text-muted small mb-0">
                        Weekly aggregates (Fri→Thu) from Jungle Scout. Upload a file containing your ASINs to get started.
                    </p>
                </div>

                {/* Informational section */}
                <div className="card border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-white border-0 pt-4 pb-0 px-4 px-lg-5">
                        <h6 className="fw-bold d-flex align-items-center gap-2 mb-0" style={{ color: '#0f172a' }}>
                            <i className="bi bi-journal-text fs-5 text-primary"></i> Instructions
                        </h6>
                    </div>
                    <div className="card-body p-4 p-lg-5 pt-3">
                        <div className="row g-4">
                            <div className="col-md-4 d-flex gap-3">
                                <div className="flex-shrink-0">
                                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>1</span>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-1 text-slate-700" style={{ fontSize: '0.95rem' }}>Upload File</h6>
                                    <p className="text-muted small mb-0">Provide a <code>.csv</code> or <code>.txt</code> file containing one <code>ASIN,Brand</code> pair per line (first line can be a header).</p>
                                </div>
                            </div>
                            <div className="col-md-4 d-flex gap-3">
                                <div className="flex-shrink-0">
                                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>2</span>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-1 text-slate-700" style={{ fontSize: '0.95rem' }}>Jungle Scout API</h6>
                                    <p className="text-muted small mb-0">Queries JS API for weekly aggregates (Fri → Thu). Max 10 ASINs per query.</p>
                                </div>
                            </div>
                            <div className="col-md-4 d-flex gap-3">
                                <div className="flex-shrink-0">
                                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>3</span>
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-1 text-slate-700" style={{ fontSize: '0.95rem' }}>Token Limit</h6>
                                    <p className="text-muted small mb-0">Each ASIN uses <strong>1 token</strong>. The limit is <strong>1,000 tokens/month</strong>.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Token usage badge */}
                {tokenInfo && (
                    <div className="mb-4 d-flex align-items-center gap-2 flex-wrap justify-content-end">
                        <span className={`badge ${tokenBadgeClass()} px-3 py-2 d-flex align-items-center gap-2`} style={{ fontSize: '0.875rem', borderRadius: '20px' }}>
                            <i className="bi bi-ticket-perforated"></i> Tokens: {tokenInfo.used} / {tokenInfo.limit} used
                        </span>
                        <span className="text-muted small">({tokenInfo.remaining} remaining this month)</span>
                        {tokenInfo.used >= tokenInfo.limit * 0.9 && (
                            <div className="alert alert-danger mb-0 py-1 px-3 d-inline-flex align-items-center gap-2" style={{ borderRadius: '6px', fontSize: '0.85rem' }}>
                                <i className="bi bi-exclamation-triangle-fill"></i> Approaching monthly limit.
                            </div>
                        )}
                    </div>
                )}

                {/* Input form — file upload */}
                <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '16px', background: '#ffffff' }}>
                    <div className="card-body p-4 p-lg-5">
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <div className="bg-primary bg-opacity-10 text-primary p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                <i className="bi bi-cloud-arrow-up fs-4"></i>
                            </div>
                            <div>
                                <h5 className="fw-bold mb-0 text-slate-800" style={{ color: '#0f172a' }}>Data Source</h5>
                                <p className="text-muted small mb-0">Select your file, marketplace, and preferred metric to estimate sales.</p>
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.9rem', color: '#334155' }}>
                                    ASINs Input Method
                                </label>
                                <div className="d-flex gap-2 mb-3">
                                    <button
                                        type="button"
                                        className={`btn btn-sm px-3 py-1.5 fw-semibold ${inputType === 'file' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleTabChange('file')}
                                        style={{ borderRadius: '20px', fontSize: '0.85rem' }}
                                    >
                                        <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload File
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm px-3 py-1.5 fw-semibold ${inputType === 'text' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleTabChange('text')}
                                        style={{ borderRadius: '20px', fontSize: '0.85rem' }}
                                    >
                                        <i className="bi bi-pencil-square me-1"></i> Paste ASINs
                                    </button>
                                </div>

                                {inputType === 'file' ? (
                                    <>
                                        <input
                                            type="file"
                                            className="form-control form-control-lg"
                                            accept=".txt,.csv"
                                            onChange={handleFileChange}
                                            style={{ borderRadius: '10px', fontSize: '0.9rem' }}
                                        />
                                        <small className="text-muted d-block mt-2" style={{ fontSize: '0.75rem' }}>
                                            Supported formats: <strong>.csv</strong> or <strong>.txt</strong> (Max 10).
                                        </small>
                                    </>
                                ) : (
                                    <>
                                        <textarea
                                            className="form-control form-control-lg"
                                            rows={2}
                                            placeholder="ASIN,Brand"
                                            value={textAsins}
                                            onChange={handleTextAsinsChange}
                                            style={{ borderRadius: '10px', fontSize: '0.9rem', resize: 'none' }}
                                        />
                                        <small className="text-muted d-block mt-2" style={{ fontSize: '0.75rem' }}>
                                            Max 10 ASINs. Format: <code>ASIN,Brand</code>
                                        </small>
                                    </>
                                )}
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.9rem', color: '#334155' }}>Marketplace</label>
                                <select
                                    className="form-select form-select-lg"
                                    value={marketplace}
                                    onChange={e => setMarketplace(e.target.value)}
                                    style={{ borderRadius: '10px', fontSize: '0.9rem' }}
                                >
                                    <option value="us">US</option>
                                    <option value="mx">MX</option>
                                    <option value="ca">CA</option>
                                    <option value="uk">UK</option>
                                    <option value="de">DE</option>
                                    <option value="fr">FR</option>
                                    <option value="it">IT</option>
                                    <option value="es">ES</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.9rem', color: '#334155' }}>Date Range</label>
                                <select
                                    className="form-select form-select-lg"
                                    value={dateRange}
                                    onChange={e => setDateRange(e.target.value)}
                                    style={{ borderRadius: '10px', fontSize: '0.9rem' }}
                                >
                                    <option value="4weeks">4 Weeks</option>
                                    <option value="3months">3 Months</option>
                                    <option value="6months">6 Months</option>
                                    <option value="1year">1 Year</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.9rem', color: '#334155' }}>Metric</label>
                                <div className="btn-group w-100" role="group" style={{ height: 'calc(1.5em + 1rem + 2px)' }}>
                                    <button className={`btn d-flex align-items-center justify-content-center ${metricMode === 'sales_amount' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMetricMode('sales_amount')}
                                        style={{ borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px', fontSize: '0.9rem' }}
                                    >
                                        <i className="bi bi-currency-dollar me-1"></i> Sales
                                    </button>
                                    <button className={`btn d-flex align-items-center justify-content-center ${metricMode === 'units_sold' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setMetricMode('units_sold')}
                                        style={{ borderTopRightRadius: '10px', borderBottomRightRadius: '10px', fontSize: '0.9rem' }}
                                    >
                                        <i className="bi bi-box me-1"></i> Units
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-2 d-flex align-items-start">
                                <div className="w-100">
                                    <label className="form-label mb-2 d-none d-md-block">&nbsp;</label>
                                    <button
                                        className="btn btn-primary w-100 d-flex align-items-center justify-content-center fw-bold shadow-sm"
                                        onClick={handleSubmit}
                                        disabled={loading || parsedAsins.length === 0}
                                        style={{ borderRadius: '10px', height: 'calc(1.5em + 1rem + 2px)', fontSize: '0.95rem' }}
                                    >
                                        {loading ? (
                                            <><span className="spinner-border spinner-border-sm me-2" />Running</>
                                        ) : (
                                            <><i className="bi bi-play-fill me-1 fs-5"></i> Run</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Preview of parsed ASINs */}

                        {(parsedAsins.length > 0) && (
                            <div className="mt-3">
                                <span className="badge bg-primary me-2">{parsedAsins.length} ASINs detected</span>
                                <span className="text-muted small">
                                    This query will use <strong>{parsedAsins.length}</strong> token(s)
                                </span>
                                <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                                        <thead className="table-light">
                                            <tr><th>#</th><th>ASIN</th><th>Brand</th></tr>
                                        </thead>
                                        <tbody>
                                            {parsedAsins.map((a, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td><code>{a.asin}</code></td>
                                                    <td>{a.brand}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {inputType === 'file' && file && parsedAsins.length === 0 && (
                            <div className="alert alert-warning mt-3 mb-0 py-2 d-flex align-items-center gap-2">
                                <i className="bi bi-exclamation-triangle-fill"></i> No valid ASINs found in file. Check the format: <code>ASIN,Brand</code> per line.
                            </div>
                        )}

                        {inputType === 'text' && textAsins.trim() !== '' && parsedAsins.length === 0 && (
                            <div className="alert alert-warning mt-3 mb-0 py-2 d-flex align-items-center gap-2">
                                <i className="bi bi-exclamation-triangle-fill"></i> No valid ASINs found. Check the format: <code>ASIN</code> or <code>ASIN,Brand</code> per line.
                            </div>
                        )}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-center mb-4" role="alert" style={{ borderRadius: '8px', background: '#fef2f2', color: '#b91c1c' }}>
                        <i className="bi bi-exclamation-circle-fill fs-5 me-3"></i>
                        <div style={{ fontSize: '0.875rem' }}>{error}</div>
                    </div>
                )}

                {/* Failures warning */}
                {safeResult.failures && safeResult.failures.length > 0 && (
                    <div className="alert alert-warning border-0 shadow-sm d-flex flex-column gap-1" style={{ borderRadius: '8px' }}>
                        <strong className="d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill text-warning"></i> Failed ASINs:
                        </strong>
                        <ul className="mb-0 mt-1 small">
                            {safeResult.failures.map((f, i) => (
                                <li key={i}><code>{f.asin}</code>: {f.error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Results */}
                {safeResult.weekly.length > 0 || safeResult.highlights || safeResult.failures.length > 0 ? (
                    <>
                        {/* Date range info */}
                        <div className="mb-3 text-muted small d-flex align-items-center gap-3">
                            <span>
                                <i className="bi bi-calendar3 me-1"></i> Date Range: <strong>{safeResult.dateRange.startDate}</strong> &rarr; <strong>{safeResult.dateRange.endDate}</strong>
                            </span>
                            <span>|</span>
                            <span>
                                Tokens used: <strong>{safeResult.tokensUsed}</strong> (Remaining: <strong>{safeResult.tokensRemaining}</strong>)
                            </span>
                        </div>

                        {/* Highlights */}
                        <h4 className="mb-3 fw-bold text-slate-800" style={{ fontSize: '1.25rem' }}>Highlights (selected week)</h4>
                        {renderHighlights()}

                        {/* Overview graph */}
                        {overviewData && (
                            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                <div className="card-body p-4">
                                    <Plot
                                        data={overviewData.traces}
                                        layout={overviewData.layout}
                                        config={{ responsive: true, displayModeBar: false }}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '450px' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Additional Metrics Row */}
                        <div className="row g-4 mb-4">
                            {/* Price Elasticity */}
            {safeResult.weekly.length > 0 && (
                <div className="card shadow-sm border-0 mb-4 rounded-4" style={{ backgroundColor: '#fff' }}>
                    <div className="card-body p-4">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="fw-bold mb-0 text-dark">
                                <i className="bi bi-graph-down text-info me-2"></i>
                                Price Elasticity
                                <i className="bi bi-question-circle ms-2 text-muted" style={{ fontSize: '0.9rem', cursor: 'pointer' }}
                                    title="Price Elasticity of Demand (PED) measures how sensitive customer demand is to price changes. A highly negative number (e.g. -3.0) means demand drops significantly when price increases (Elastic). A number close to 0 means demand stays the same regardless of price (Inelastic). Outliers are automatically labeled with Seasonal Events (e.g. Black Friday) to avoid distorting analysis."></i>
                            </h6>
                        </div>
                        {elasticityData && (
                            <Plot
                                data={elasticityData.data}
                                layout={elasticityData.layout}
                                config={{ responsive: true, displayModeBar: false }}
                                useResizeHandler={true}
                                style={{ width: '100%', height: '320px' }}
                            />
                        )}
                    </div>
                </div>
            )}
                        </div>

                        {/* Breakdown by brand */}
                        {breakdownData && breakdownData.length > 0 && (
                            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 text-slate-800" style={{ color: '#0f172a' }}>
                                        <i className="bi bi-bar-chart-steps text-success"></i> Breakdown by Brand
                                    </h5>
                                    <div className="row g-3">
                                        {breakdownData.map((bd, i) => (
                                            <div className="col-lg-4 col-md-6 mb-3" key={bd.brand}>
                                                <Plot
                                                    data={[bd.trace]}
                                                    layout={{
                                                        title: {
                                                            text: `<b>${bd.brand}</b>`,
                                                            font: { family: 'Inter, system-ui, sans-serif', size: 13, color: '#334155' }
                                                        },
                                                        height: 250,
                                                        margin: { l: 60, r: 10, t: 45, b: 55 },
                                                        showlegend: false,
                                                        xaxis: { type: 'date', tickformat: '%b %d', tickangle: 0, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 9, color: '#64748b' } },
                                                        yaxis: { title: { text: metricMode === 'sales_amount' ? 'Sales (USD)' : 'Units Sold', font: { size: 10 } }, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 9, color: '#64748b' } },
                                                        plot_bgcolor: 'rgba(0,0,0,0)',
                                                        paper_bgcolor: 'rgba(0,0,0,0)',
                                                    }}
                                                    config={{ responsive: true, displayModeBar: false }}
                                                    useResizeHandler={true}
                                                    style={{ width: '100%', height: '250px' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Weekly data table (expandable like tracking2) */}
                        {safeResult.weekly.length > 0 && (
                            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', overflow: 'hidden' }}>
                                <div className="card-body p-4">
                                    <details>
                                        <summary className="fw-bold mb-2 text-slate-800" style={{ cursor: 'pointer' }}>
                                            Show weekly table (with ASIN links)
                                        </summary>
                                        <div className="d-flex justify-content-end mb-2">
                                            <button className="btn btn-sm btn-outline-success" onClick={handleDownloadCSV}>
                                                <i className="bi bi-download me-1"></i> Download CSV
                                            </button>
                                        </div>
                                        <div className="table-responsive mt-2">
                                            <table className="table table-hover table-sm mb-0">
                                                <thead>
                                                    <tr style={{ background: '#f8fafc', color: '#475569', fontSize: '0.85rem' }}>
                                                        <th className="p-2 border-bottom">Week End</th>
                                                        <th className="p-2 border-bottom">Brand</th>
                                                        <th className="p-2 border-bottom">ASIN</th>
                                                        <th className="p-2 border-bottom text-end">Units Sold</th>
                                                        <th className="p-2 border-bottom text-end">Sales ($)</th>
                                                        <th className="p-2 border-bottom text-end">Avg Price ($)</th>
                                                        <th className="p-2 border-bottom">Amazon</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeResult.weekly.map((row, i) => (
                                                        <tr key={i} style={{ fontSize: '0.85rem' }}>
                                                            <td className="p-2">{row.week_end}</td>
                                                            <td className="p-2"><span className="badge bg-secondary">{row.brand}</span></td>
                                                            <td className="p-2"><code>{row.asin}</code></td>
                                                                <td className="p-2 text-end">
                                                                    {row.units_sold.toLocaleString()}
                                                                    {renderVar(wowVars[`${row.asin}|${row.week_end}`]?.unitsPct)}
                                                                </td>
                                                                <td className="p-2 text-end">
                                                                    ${row.sales_amount.toLocaleString()}
                                                                    {renderVar(wowVars[`${row.asin}|${row.week_end}`]?.salesPct)}
                                                                </td>
                                                            <td className="p-2 text-end">${row.avg_price.toFixed(2)}</td>
                                                            <td className="p-2">
                                                                <a href={`https://www.${MARKETPLACE_DOMAINS[marketplace] || 'amazon.com'}/dp/${row.asin}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" style={{ borderRadius: '6px', padding: '2px 8px' }}>
                                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
            
            {/* Amazon Events Manager Modal */}
            {showEventsManager && (
                <AmazonEventsManager
                    events={amazonEvents}
                    onEventsChanged={loadAmazonEvents}
                    onClose={() => setShowEventsManager(false)}
                />
            )}
        </Layout>
    );
}

export default SalesEstimate;